import React, { useEffect, useRef, useState } from 'react';
import PlayerPopupHeader, { canLinkHallProfile } from './PlayerPopupHeader';
import { getPlayerAudioPref } from '../playerAudio';
import { chatTimeLabel, ChatMessageBody } from './HallChatPanel';

function authorMatches(msg, player) {
  if (!msg || !player) return false;
  if (player.userId != null && msg.userId != null) {
    return String(msg.userId) === String(player.userId);
  }
  return msg.username === player.username;
}

const PlayerAudioPanel = ({
  player,
  playerAudio,
  onClose,
  onMuteToggle,
  onVolumeChange,
  liveMessage,
  onVideoMomentClick,
  onClipClick,
  canManageStage = false,
  teamAFull = false,
  teamBFull = false,
  onToggleVip,
  onTeleportTeam
}) => {
  const [profile, setProfile] = useState(null);
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const listRef = useRef(null);
  const pinBottomRef = useRef(true);
  const loadingRef = useRef(false);
  const pendingRestoreRef = useRef(null);
  const pref = player?.id ? getPlayerAudioPref(playerAudio, player.id) : { muted: false, volume: 1 };
  const canLink = canLinkHallProfile(player);
  const showVolume = !!(player?.id && onMuteToggle && onVolumeChange);
  const hallSlug = player?.hallSlug;

  useEffect(() => {
    if (!canLink || !player.username) {
      setProfile(null);
      return undefined;
    }
    let cancelled = false;
    fetch(`/api/users/${encodeURIComponent(player.username)}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && data) setProfile(data);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [canLink, player.username]);

  useEffect(() => {
    if (!hallSlug || (!player.username && player.userId == null)) {
      setMessages([]);
      setHasMore(false);
      return undefined;
    }
    let cancelled = false;
    const params = new URLSearchParams({ limit: '20' });
    if (player.userId != null && String(player.userId) !== 'guest' && !String(player.userId).startsWith('steam-')) {
      params.set('userId', String(player.userId));
    } else if (player.username) {
      params.set('username', player.username);
    }
    fetch(`/api/halls/${encodeURIComponent(hallSlug)}/chat/messages?${params}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        setMessages(data.messages || []);
        setHasMore(!!data.hasMore);
        pinBottomRef.current = true;
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [hallSlug, player.userId, player.username]);

  useEffect(() => {
    if (!liveMessage || !authorMatches(liveMessage, player)) return;
    setMessages((prev) => {
      if (prev.some((m) => m.id === liveMessage.id)) return prev;
      return [...prev, liveMessage];
    });
  }, [liveMessage, player]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    if (pendingRestoreRef.current != null) {
      el.scrollTop = el.scrollHeight - pendingRestoreRef.current;
      pendingRestoreRef.current = null;
      return;
    }
    if (pinBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  const loadOlder = () => {
    if (!hasMore || loadingRef.current || !messages.length || !hallSlug) return;
    const oldest = messages[0];
    loadingRef.current = true;
    setLoading(true);
    const params = new URLSearchParams({ limit: '20', beforeId: String(oldest.id) });
    if (player.userId != null && String(player.userId) !== 'guest' && !String(player.userId).startsWith('steam-')) {
      params.set('userId', String(player.userId));
    } else if (player.username) {
      params.set('username', player.username);
    }
    const prevHeight = listRef.current ? listRef.current.scrollHeight : 0;
    pendingRestoreRef.current = prevHeight;
    fetch(`/api/halls/${encodeURIComponent(hallSlug)}/chat/messages?${params}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data) {
          pendingRestoreRef.current = null;
          return;
        }
        setMessages((prev) => {
          const ids = new Set(prev.map((m) => m.id));
          const older = (data.messages || []).filter((m) => !ids.has(m.id));
          return [...older, ...prev];
        });
        setHasMore(!!data.hasMore);
      })
      .catch(() => {})
      .finally(() => {
        loadingRef.current = false;
        setLoading(false);
      });
  };

  const handleScroll = () => {
    const el = listRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    pinBottomRef.current = distanceFromBottom < 24;
    if (el.scrollTop < 32) loadOlder();
  };

  const volumePct = Math.round(pref.volume * 100);

  return (
    <div
      className="fixed inset-0 z-[150] flex items-center justify-center bg-black bg-opacity-60"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-gray-900 border border-gray-700 rounded-lg shadow-xl w-96 max-w-[90vw] h-[28rem] max-h-[90vh] text-white p-4 flex flex-col"
      >
        <PlayerPopupHeader
          username={profile?.username || player.username}
          steamId64={profile?.steamId64 || player.steamId64}
          youtubeChannelId={profile?.youtubeChannelId || player.youtubeChannelId}
          xp={typeof profile?.xp === 'number' ? profile.xp : (typeof player.xp === 'number' ? player.xp : 0)}
          hallXp={typeof player.hallXp === 'number' ? player.hallXp : undefined}
          hallSlug={player.hallSlug}
          onClose={onClose}
          canLinkProfile={canLink}
        />

        {canManageStage && player?.id && (
          <div className="flex flex-wrap gap-2 mb-3">
            <button
              type="button"
              onClick={() => onToggleVip?.(!player.isVip)}
              className={`py-1.5 px-3 rounded text-xs font-semibold ${
                player.isVip
                  ? 'bg-yellow-600 hover:bg-yellow-500'
                  : 'bg-gray-700 hover:bg-gray-600'
              }`}
            >
              {player.isVip ? 'VIP on' : 'VIP'}
            </button>
            <button
              type="button"
              disabled={teamAFull}
              onClick={() => onTeleportTeam?.('A')}
              className="py-1.5 px-3 rounded text-xs font-semibold bg-blue-800 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Team A seat
            </button>
            <button
              type="button"
              disabled={teamBFull}
              onClick={() => onTeleportTeam?.('B')}
              className="py-1.5 px-3 rounded text-xs font-semibold bg-red-800 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Team B seat
            </button>
          </div>
        )}
        {showVolume && (
          <>
            <div className="flex items-center justify-between text-xs text-gray-300 mb-1">
              <span>Incoming volume</span>
              <span className="tabular-nums">{volumePct}%</span>
            </div>
            <div className="flex items-center gap-3 mb-3">
              <button
                type="button"
                onClick={onMuteToggle}
                className={`flex-shrink-0 py-1.5 px-3 rounded text-xs font-semibold ${
                  pref.muted
                    ? 'bg-red-700 hover:bg-red-600'
                    : 'bg-gray-700 hover:bg-gray-600'
                }`}
              >
                {pref.muted ? 'Unmute' : 'Mute'}
              </button>
              <input
                type="range"
                min="0"
                max="100"
                step="1"
                value={volumePct}
                disabled={pref.muted}
                onChange={(e) => onVolumeChange(Number(e.target.value) / 100)}
                className="flex-1 min-w-0 h-1.5 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-500 disabled:opacity-40"
              />
            </div>
          </>
        )}

        <div
          ref={listRef}
          onScroll={handleScroll}
          className="flex-1 min-h-0 overflow-y-auto border-t border-gray-800 pt-2 space-y-1.5 text-xs bp-hide-scrollbar"
        >
          {hasMore && (
            <div className="text-center text-gray-500 py-1">
              {loading ? 'Loading…' : 'Scroll for older messages'}
            </div>
          )}
          {messages.length === 0 && (
            <div className="text-gray-500 text-center py-4">No hall messages yet.</div>
          )}
          {messages.map((msg) => (
            <div key={msg.id} className="flex gap-2">
              <span className="text-gray-500 tabular-nums whitespace-nowrap flex-shrink-0">
                {chatTimeLabel(msg.createdAt)}
              </span>
              <span className="min-w-0">
                <ChatMessageBody
                  text={msg.body}
                  onVideoMomentClick={onVideoMomentClick}
                  onClipClick={onClipClick}
                />
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PlayerAudioPanel;
